import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { publicFeedbackService } from '../services/publicFeedbackService';
import { FiStar, FiCheck, FiAlertCircle } from 'react-icons/fi';

const ClientFeedback = () => {
  const { invoiceNumber } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [client, setClient] = useState(null);
  const [appointment, setAppointment] = useState(null);

  // Feedback form state
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [wouldRecommend, setWouldRecommend] = useState(null);

  useEffect(() => {
    loadFeedbackData();
  }, [invoiceNumber, token]);

  const loadFeedbackData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!invoiceNumber || !token) {
        setError('Missing invoice number or security token');
        return;
      }

      // Validate token format (basic check)
      if (token.length < 10) {
        setError('Invalid security token');
        return;
      }

      console.log('Loading feedback data for invoice number:', invoiceNumber);

      // Get invoice data by invoice number
      const invoiceData = await publicFeedbackService.getInvoiceByNumber(invoiceNumber);
      if (!invoiceData) {
        setError('Invoice not found');
        return;
      }

      setInvoice(invoiceData);
      console.log('Invoice loaded:', invoiceData);

      // Check if feedback already exists for this invoice number
      const existingFeedback = await publicFeedbackService.checkExistingFeedback(
        invoiceData.tenantId,
        invoiceNumber
      );

      if (existingFeedback) {
        setSubmitted(true);
        return;
      }

      // Load appointment data
      if (invoiceData.appointmentId) {
        const appointmentData = await publicFeedbackService.getAppointmentById(invoiceData.tenantId, invoiceData.appointmentId);
        setAppointment(appointmentData);
      }

      // Load client data
      if (invoiceData.clientId) {
        const clientData = await publicFeedbackService.getClientById(invoiceData.tenantId, invoiceData.clientId);
        setClient(clientData);
      }

    } catch (error) {
      console.error('Error loading feedback data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();

    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      const feedbackData = {
        invoiceId: invoice.id,
        invoiceNumber: invoiceNumber,
        clientId: invoice.clientId || client?.id || 'unknown',
        appointmentId: invoice.appointmentId || null,
        rating: rating,
        comment: comment.trim(),
        wouldRecommend: wouldRecommend,
        submittedAt: new Date().toISOString(),
        clientEmail: client?.email || 'unknown',
        clientName: client?.name || invoice.clientName || 'Unknown Client',
        treatmentType: appointment?.type || 'Unknown Treatment',
        appointmentDate: appointment?.date || null
      };

      await publicFeedbackService.submitFeedback(invoice.tenantId, feedbackData);

      setSubmitted(true);
      toast.success('Thank you for your feedback!');

    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading feedback form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-sm p-8 text-center">
          <FiAlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Feedback Form</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">
            If you believe this is an error, please contact the clinic directly.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-sm p-8 text-center">
          <FiCheck className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Thank You!</h1>
          <p className="text-gray-600 mb-4">
            Your feedback has been submitted successfully. We appreciate you taking the time to help us improve our services.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              Your feedback helps us provide better care for all our patients. Thank you for choosing our clinic!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6 text-white">
            <h1 className="text-2xl font-bold mb-2">⭐ Share Your Feedback</h1>
            <p className="text-blue-100">Help us improve our healthcare services</p>
          </div>

          {/* Invoice Info */}
          <div className="px-8 py-6 bg-gray-50 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">About Your Visit</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Patient:</span>
                <span className="ml-2 text-gray-900">{client?.name || invoice?.clientName || 'Unknown'}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Invoice:</span>
                <span className="ml-2 text-gray-900">{invoice?.invoiceNumber}</span>
              </div>
              {appointment && (
                <>
                  <div>
                    <span className="font-medium text-gray-700">Treatment:</span>
                    <span className="ml-2 text-gray-900">{appointment.type}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Date:</span>
                    <span className="ml-2 text-gray-900">
                      {new Date(appointment.date).toLocaleDateString()}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Feedback Form */}
          <form onSubmit={handleSubmitFeedback} className="px-8 py-6">
            {/* Rating */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Overall Rating *
              </label>
              <div className="flex items-center space-x-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${star <= (hoverRating || rating)
                        ? 'text-yellow-400'
                        : 'text-gray-300 hover:text-yellow-300'
                      }`}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setRating(star)}
                  >
                    <FiStar className="w-6 h-6" fill={star <= (hoverRating || rating) ? 'currentColor' : 'none'} />
                  </button>
                ))}
                <span className="ml-4 text-sm text-gray-600">
                  {rating === 0 && 'Click to rate'}
                  {rating === 1 && 'Poor'}
                  {rating === 2 && 'Fair'}
                  {rating === 3 && 'Good'}
                  {rating === 4 && 'Very Good'}
                  {rating === 5 && 'Excellent'}
                </span>
              </div>
            </div>

            {/* Would Recommend */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Would you recommend our clinic to others?
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="recommend"
                    value="yes"
                    checked={wouldRecommend === true}
                    onChange={() => setWouldRecommend(true)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Yes</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="recommend"
                    value="no"
                    checked={wouldRecommend === false}
                    onChange={() => setWouldRecommend(false)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">No</span>
                </label>
              </div>
            </div>

            {/* Comments */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Additional Comments (Optional)
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Tell us about your experience, what we did well, or how we can improve..."
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">
                {comment.length}/500 characters
              </p>
            </div>

            {/* Submit Button */}
            <div className="flex justify-between items-center">
              <p className="text-xs text-gray-500">
                * Required fields. Your feedback is anonymous and helps us improve our services.
              </p>
              <button
                type="submit"
                disabled={submitting || rating === 0}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${submitting || rating === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                  }`}
              >
                {submitting ? (
                  <span className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </span>
                ) : (
                  'Submit Feedback'
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Privacy Notice */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Your feedback is collected to improve our healthcare services. We respect your privacy and will not share your individual responses with third parties.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClientFeedback;
